import React, { useEffect, useState, useCallback, Suspense, lazy } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { HitApi } from '../../Api/ApiHit'
import {
    getWebsiteConfig, updateWebsiteConfig,
    searchWebsiteSection, addWebsiteSection, updateWebsiteSection,
    deleteWebsiteSection, toggleWebsiteSectionVisibility,
    reorderWebsiteSections, seedDefaultWebsiteSections
} from '../../constant/Constant'
import { getSelectedUnit } from '../../storage/Storage'
import { setApiJson } from '../../redux/Actions/ApiAction'
import FileuploadComp from '../../components/SingleFileUpload/FileuploadComp'
import AppButton from '../../components/AppButton/AppButton'
import AppInput from '../../components/AppInput/AppInput'
import CustomModal from '../../components/CustomModal/CustumModal'
import DeleteConfirmationModal from '../../components/CustomModal/DeleteConfirmationModal'
import toast from 'react-hot-toast'
import {
    Loader2, Globe, Plus, Trash2, X, Eye, EyeOff,
    ChevronUp, ChevronDown, Pencil, RefreshCw, Layers,
    Palette, Save, Check, AlertCircle, Link, MapPin,
    Maximize2, Minimize2, Code2
} from 'lucide-react'

const MonacoEditor = lazy(() => import('@monaco-editor/react'))

// ─── Constants ────────────────────────────────────────────────────────────────

const SECTION_TYPES = [
    { value: 'hero', label: 'Hero Section' },
    { value: 'services', label: 'Services' },
    { value: 'why_choose_us', label: 'Why Choose Us' },
    { value: 'stats', label: 'Stats / Numbers' },
    { value: 'testimonials', label: 'Testimonials' },
    { value: 'cta', label: 'Call to Action' },
    { value: 'custom', label: 'Custom Section' },
]

const SECTION_LABELS = Object.fromEntries(SECTION_TYPES.map(t => [t.value, t.label]))

const DEFAULT_CONTENT = {
    hero: {
        headline: 'Feel Elevated. Look Elevated.',
        subheadline: 'Premium salon and spa services at your fingertips.',
        badge: 'Now Available',
        video_url: '',
        cta_primary: { label: 'Book Now', href: '/services' },
        cta_secondary: { label: 'Explore Services', href: '/services' },
    },
    services: { title: 'Our Services', subtitle: 'Everything you need, in one place.', items: [] },
    why_choose_us: { title: 'Why Choose Elevate?', subtitle: 'We set the standard.', features: [] },
    stats: { title: 'Our Numbers', items: [] },
    testimonials: { title: 'What Our Clients Say', items: [] },
    cta: { title: 'Ready to Elevate?', subtitle: 'Book your session today.', cta: { label: 'Book Now', href: '/services' }, backgroundImage: '' },
    custom: { html: '' },
}

// ─── Custom HTML Editor (fullscreen + preview) ────────────────────────────────

const CustomHtmlEditor = ({ content, onChange, isFullscreen, onToggleFullscreen }) => {
    const [showPreview, setShowPreview] = useState(false)

    const editorPanel = (
        <Suspense fallback={<div className="h-full bg-gray-900 flex items-center justify-center text-sm text-gray-400">Loading editor...</div>}>
            <MonacoEditor
                height="100%"
                language="html"
                theme="vs-dark"
                value={content.html || ''}
                onChange={(value) => onChange('html', value || '')}
                options={{
                    minimap: { enabled: false },
                    fontSize: 13,
                    wordWrap: 'on',
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    tabSize: 2,
                    formatOnPaste: true,
                    lineNumbers: 'on',
                }}
            />
        </Suspense>
    )

    const toolbar = (
        <div className="flex items-center justify-between px-3 py-2 bg-gray-800 border-b border-gray-700 flex-shrink-0">
            <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-red-500" />
                <span className="w-3 h-3 rounded-full bg-yellow-500" />
                <span className="w-3 h-3 rounded-full bg-green-500" />
                <span className="ml-2 text-xs text-gray-400 font-mono">index.html</span>
            </div>
            <div className="flex items-center gap-1">
                <button
                    onClick={() => setShowPreview(p => !p)}
                    className={`flex items-center gap-1.5 px-3 py-1 rounded text-xs font-medium transition-colors ${showPreview ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                >
                    <Eye className="w-3.5 h-3.5" />
                    Preview
                </button>
                <button
                    onClick={onToggleFullscreen}
                    className="flex items-center gap-1.5 px-3 py-1 rounded text-xs font-medium bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors"
                >
                    {isFullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
                    {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                </button>
            </div>
        </div>
    )

    const previewPanel = (
        <div className="flex-1 bg-white overflow-auto min-w-0">
            <div className="px-3 py-2 bg-gray-100 border-b border-gray-200 flex items-center gap-2">
                <Eye className="w-3.5 h-3.5 text-gray-500" />
                <span className="text-xs text-gray-500 font-medium">Live Preview</span>
            </div>
            <iframe
                title="preview"
                srcDoc={`<!DOCTYPE html><html><head><meta charset="UTF-8"><style>body{margin:0;font-family:sans-serif}*{box-sizing:border-box}</style></head><body>${content.html || '<p style="color:#9ca3af;padding:24px;text-align:center">Start typing to see preview...</p>'}</body></html>`}
                className="w-full h-full border-0"
                style={{ minHeight: isFullscreen ? 'calc(100vh - 44px)' : '360px' }}
                sandbox="allow-scripts"
            />
        </div>
    )

    if (isFullscreen) {
        return (
            <div className="flex flex-col h-full bg-gray-900">
                {toolbar}
                <div className="flex flex-1 overflow-hidden">
                    <div className={showPreview ? 'w-1/2' : 'w-full'} style={{ height: 'calc(100vh - 44px)' }}>
                        {editorPanel}
                    </div>
                    {showPreview && previewPanel}
                </div>
            </div>
        )
    }

    return (
        <div>
            <div className="border border-slate-300 rounded-lg overflow-hidden">
                {toolbar}
                <div className="flex" style={{ height: '360px' }}>
                    <div className={showPreview ? 'w-1/2' : 'w-full'} style={{ height: '360px' }}>
                        {editorPanel}
                    </div>
                    {showPreview && previewPanel}
                </div>
            </div>
            <p className="text-xs text-gray-400 mt-1">Use inline styles for best compatibility. Tailwind classes also supported.</p>
        </div>
    )
}

// ─── Section Content Editor ───────────────────────────────────────────────────

const SectionContentEditor = ({ type, content, onChange, onArrayChange, onAddItem, onRemoveItem, isFullscreen, onToggleFullscreen }) => {
    const fieldInput = (label, key, placeholder, nested) => (
        <AppInput
            title={label}
            placeholder={placeholder}
            value={nested ? content[nested]?.[key] || '' : content[key] || ''}
            onChangeInput={(e) => nested
                ? onChange(nested, { ...content[nested], [key]: e.target.value })
                : onChange(key, e.target.value)
            }
        />
    )

    if (type === 'hero') {
        return (
            <div>
                {fieldInput('Badge Text', 'badge', 'Now Available')}
                {fieldInput('Headline', 'headline', 'Feel Elevated. Look Elevated.')}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subheadline</label>
                    <textarea
                        value={content.subheadline || ''}
                        onChange={(e) => onChange('subheadline', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 hover:border-slate-400 mb-4"
                        rows={2}
                        placeholder="Premium salon and spa services..."
                    />
                </div>
                {fieldInput('Background Video URL', 'video_url', 'https://cdn.example.com/hero.mp4')}
                <div className="grid grid-cols-2 gap-3">
                    <AppInput title="Primary Button Label" placeholder="Book Now"
                        value={content.cta_primary?.label || ''}
                        onChangeInput={(e) => onChange('cta_primary', { ...content.cta_primary, label: e.target.value })} />
                    <AppInput title="Primary Button URL" placeholder="/services"
                        value={content.cta_primary?.href || ''}
                        onChangeInput={(e) => onChange('cta_primary', { ...content.cta_primary, href: e.target.value })} />
                    <AppInput title="Secondary Button Label" placeholder="Explore Services"
                        value={content.cta_secondary?.label || ''}
                        onChangeInput={(e) => onChange('cta_secondary', { ...content.cta_secondary, label: e.target.value })} />
                    <AppInput title="Secondary Button URL" placeholder="/services"
                        value={content.cta_secondary?.href || ''}
                        onChangeInput={(e) => onChange('cta_secondary', { ...content.cta_secondary, href: e.target.value })} />
                </div>
            </div>
        )
    }

    if (type === 'services' || type === 'why_choose_us') {
        const arrayKey = type === 'services' ? 'items' : 'features'
        const itemLabel = type === 'services' ? 'Service' : 'Feature'
        return (
            <div>
                <div className="grid grid-cols-2 gap-3">
                    <AppInput title="Section Title" placeholder="Our Services"
                        value={content.title || ''} onChangeInput={(e) => onChange('title', e.target.value)} />
                    <AppInput title="Subtitle" placeholder="Everything you need, in one place."
                        value={content.subtitle || ''} onChangeInput={(e) => onChange('subtitle', e.target.value)} />
                </div>
                <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">{itemLabel}s</label>
                    <AppButton buttontext={`Add ${itemLabel}`} icon={Plus} iconPosition="left" size="sm"
                        onClick={() => onAddItem(arrayKey, { title: '', description: '', icon: '' })} />
                </div>
                {(content[arrayKey] || []).map((item, i) => (
                    <div key={i} className="mb-3 p-3 border border-gray-200 rounded-lg">
                        <div className="flex gap-2 mb-2">
                            <div className="flex-1">
                                <AppInput placeholder={`${itemLabel} title`}
                                    value={item.title || ''} onChangeInput={(e) => onArrayChange(arrayKey, i, 'title', e.target.value)} />
                            </div>
                            <div className="w-24">
                                <AppInput placeholder="Emoji" value={item.icon || ''}
                                    onChangeInput={(e) => onArrayChange(arrayKey, i, 'icon', e.target.value)} />
                            </div>
                            <button onClick={() => onRemoveItem(arrayKey, i)} className="text-red-400 hover:text-red-600 mt-1 flex-shrink-0">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <AppInput placeholder="Short description"
                            value={item.description || ''} onChangeInput={(e) => onArrayChange(arrayKey, i, 'description', e.target.value)} />
                    </div>
                ))}
                {!(content[arrayKey] || []).length && (
                    <p className="text-xs text-gray-400 text-center py-3 border border-dashed border-gray-200 rounded-lg">No items yet. Add one above.</p>
                )}
            </div>
        )
    }

    if (type === 'stats') {
        return (
            <div>
                <AppInput title="Section Title" placeholder="Our Numbers"
                    value={content.title || ''} onChangeInput={(e) => onChange('title', e.target.value)} />
                <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">Stats</label>
                    <AppButton buttontext="Add Stat" icon={Plus} iconPosition="left" size="sm"
                        onClick={() => onAddItem('items', { label: '', value: '', suffix: '+' })} />
                </div>
                {(content.items || []).map((item, i) => (
                    <div key={i} className="mb-2 p-3 border border-gray-200 rounded-lg flex gap-2 items-start">
                        <div className="flex-1">
                            <AppInput placeholder="Label (e.g. Happy Clients)"
                                value={item.label || ''} onChangeInput={(e) => onArrayChange('items', i, 'label', e.target.value)} />
                        </div>
                        <div className="w-24">
                            <AppInput placeholder="5000" value={item.value || ''}
                                onChangeInput={(e) => onArrayChange('items', i, 'value', e.target.value)} />
                        </div>
                        <div className="w-14">
                            <AppInput placeholder="+" value={item.suffix || ''}
                                onChangeInput={(e) => onArrayChange('items', i, 'suffix', e.target.value)} />
                        </div>
                        <button onClick={() => onRemoveItem('items', i)} className="text-red-400 hover:text-red-600 mt-1 flex-shrink-0">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                ))}
                {!(content.items || []).length && (
                    <p className="text-xs text-gray-400 text-center py-3 border border-dashed border-gray-200 rounded-lg">No stats yet. Add one above.</p>
                )}
            </div>
        )
    }

    if (type === 'testimonials') {
        return (
            <div>
                <AppInput title="Section Title" placeholder="What Our Clients Say"
                    value={content.title || ''} onChangeInput={(e) => onChange('title', e.target.value)} />
                <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">Testimonials</label>
                    <AppButton buttontext="Add Testimonial" icon={Plus} iconPosition="left" size="sm"
                        onClick={() => onAddItem('items', { name: '', review: '', rating: 5 })} />
                </div>
                {(content.items || []).map((item, i) => (
                    <div key={i} className="mb-3 p-3 border border-gray-200 rounded-lg">
                        <div className="flex gap-2 mb-2">
                            <div className="flex-1">
                                <AppInput placeholder="Client Name" value={item.name || ''}
                                    onChangeInput={(e) => onArrayChange('items', i, 'name', e.target.value)} />
                            </div>
                            <div className="w-20">
                                <AppInput type="number" placeholder="5" value={item.rating ?? 5}
                                    onChangeInput={(e) => onArrayChange('items', i, 'rating', parseInt(e.target.value))} />
                            </div>
                            <button onClick={() => onRemoveItem('items', i)} className="text-red-400 hover:text-red-600 mt-1 flex-shrink-0">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <textarea value={item.review || ''} onChange={(e) => onArrayChange('items', i, 'review', e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-blue-500 hover:border-slate-400"
                            rows={2} placeholder="Client review text..." />
                    </div>
                ))}
                {!(content.items || []).length && (
                    <p className="text-xs text-gray-400 text-center py-3 border border-dashed border-gray-200 rounded-lg">No testimonials yet. Add one above.</p>
                )}
            </div>
        )
    }

    if (type === 'cta') {
        return (
            <div>
                <AppInput title="Title" placeholder="Ready to Elevate?"
                    value={content.title || ''} onChangeInput={(e) => onChange('title', e.target.value)} />
                <AppInput title="Subtitle" placeholder="Book your session today."
                    value={content.subtitle || ''} onChangeInput={(e) => onChange('subtitle', e.target.value)} />
                <div className="grid grid-cols-2 gap-3">
                    <AppInput title="Button Label" placeholder="Book Now"
                        value={content.cta?.label || ''} onChangeInput={(e) => onChange('cta', { ...content.cta, label: e.target.value })} />
                    <AppInput title="Button URL" placeholder="/services"
                        value={content.cta?.href || ''} onChangeInput={(e) => onChange('cta', { ...content.cta, href: e.target.value })} />
                </div>
                <FileuploadComp title="Background Image (optional)" name="ctaBgImage" allowed={['img']} />
            </div>
        )
    }

    if (type === 'custom') {
        return <CustomHtmlEditor content={content} onChange={onChange} isFullscreen={isFullscreen} onToggleFullscreen={onToggleFullscreen} />
    }

    return <p className="text-sm text-gray-400 text-center py-8">No editor for this section type.</p>
}

// ─── Main Component ───────────────────────────────────────────────────────────

const WebsiteConfiguration = () => {
    const dispatch = useDispatch()
    const ApiReducer = useSelector(state => state.ApiReducer)

    const [activeTab, setActiveTab] = useState('settings')

    // Config state
    const [config, setConfig] = useState(null)
    const [configLoading, setConfigLoading] = useState(false)
    const [isSaving, setIsSaving] = useState(false)

    // Sections state
    const [sections, setSections] = useState([])
    const [sectionsLoading, setSectionsLoading] = useState(false)

    // Add section modal
    const [showAddSection, setShowAddSection] = useState(false)
    const [newSectionType, setNewSectionType] = useState('hero')
    const [isAddingSection, setIsAddingSection] = useState(false)

    // Edit section modal
    const [editingSection, setEditingSection] = useState(null)
    const [editContent, setEditContent] = useState({})
    const [isSavingSection, setIsSavingSection] = useState(false)

    // Delete modal
    const [deleteTarget, setDeleteTarget] = useState(null)
    const [isDeleting, setIsDeleting] = useState(false)

    // Editor fullscreen
    const [editorFullscreen, setEditorFullscreen] = useState(false)

    const unitId = getSelectedUnit()?._id || getSelectedUnit()?.[0]

    // ── Fetch Config ──────────────────────────────────────────────────────────

    const fetchConfig = useCallback(async () => {
        if (!unitId) return
        setConfigLoading(true)
        try {
            const res = await HitApi({ unitIds: unitId }, getWebsiteConfig)
            if (res?.success && res?.data) {
                setConfig(res.data)
                dispatch(setApiJson({
                    logoUrl: res.data.logoUrl || '',
                    logoUrlDark: res.data.logoUrlDark || '',
                    faviconUrl: res.data.faviconUrl || '',
                }))
            }
        } catch { }
        setConfigLoading(false)
    }, [unitId, dispatch])

    // ── Fetch Sections ────────────────────────────────────────────────────────

    const fetchSections = useCallback(async () => {
        if (!unitId) return
        setSectionsLoading(true)
        try {
            const res = await HitApi(
                { page: 1, limit: 100, search: { unitIds: unitId }, sort: { order: 1 } },
                searchWebsiteSection
            )
            setSections(res?.data?.docs || [])
        } catch { setSections([]) }
        setSectionsLoading(false)
    }, [unitId])

    useEffect(() => { fetchConfig() }, [fetchConfig])
    useEffect(() => { if (activeTab === 'sections') fetchSections() }, [activeTab, fetchSections])

    // ── Config Helpers ────────────────────────────────────────────────────────

    const updateConfigField = (path, value) => {
        setConfig(prev => {
            const updated = { ...prev }
            const keys = path.split('.')
            if (keys.length === 1) updated[keys[0]] = value
            else if (keys.length === 2) updated[keys[0]] = { ...(updated[keys[0]] || {}), [keys[1]]: value }
            else if (keys.length === 3) updated[keys[0]] = { ...(updated[keys[0]] || {}), [keys[1]]: { ...(updated[keys[0]]?.[keys[1]] || {}), [keys[2]]: value } }
            return updated
        })
    }

    const handleSaveConfig = async () => {
        if (!unitId || !config) return
        setIsSaving(true)
        const payload = {
            ...config,
            unitIds: unitId,
            logoUrl: ApiReducer?.apiJson?.logoUrl ?? config.logoUrl ?? '',
            logoUrlDark: ApiReducer?.apiJson?.logoUrlDark ?? config.logoUrlDark ?? '',
            faviconUrl: ApiReducer?.apiJson?.faviconUrl ?? config.faviconUrl ?? '',
        }
        try {
            const res = await HitApi(payload, updateWebsiteConfig)
            if (res?.success) { toast.success('Website config saved!'); fetchConfig() }
            else toast.error(res?.message || 'Failed to save')
        } catch { toast.error('Failed to save config') }
        setIsSaving(false)
    }

    // ── Nav Links ─────────────────────────────────────────────────────────────

    const addNavLink = () => setConfig(prev => ({
        ...prev,
        header: { ...(prev?.header || {}), navLinks: [...(prev?.header?.navLinks || []), { label: '', href: '' }] }
    }))

    const updateNavLink = (index, field, value) => setConfig(prev => {
        const links = [...(prev?.header?.navLinks || [])]
        links[index] = { ...links[index], [field]: value }
        return { ...prev, header: { ...(prev?.header || {}), navLinks: links } }
    })

    const removeNavLink = (index) => setConfig(prev => {
        const links = [...(prev?.header?.navLinks || [])]
        links.splice(index, 1)
        return { ...prev, header: { ...(prev?.header || {}), navLinks: links } }
    })

    // ── Social Links ──────────────────────────────────────────────────────────

    const updateSocialLink = (platform, value) => setConfig(prev => ({
        ...prev,
        footer: { ...(prev?.footer || {}), socialLinks: { ...(prev?.footer?.socialLinks || {}), [platform]: value } }
    }))

    // ── Section Actions ───────────────────────────────────────────────────────

    const handleToggleVisibility = async (section) => {
        try {
            const res = await HitApi({ _id: section._id, isVisible: !section.isVisible }, toggleWebsiteSectionVisibility)
            if (res?.success) {
                toast.success(section.isVisible ? 'Section hidden' : 'Section visible')
                setSections(prev => prev.map(s => s._id === section._id ? { ...s, isVisible: !s.isVisible } : s))
            } else toast.error('Failed to update visibility')
        } catch { toast.error('Failed to update visibility') }
    }

    const handleMoveSection = async (index, direction) => {
        const newSections = [...sections]
        const targetIndex = direction === 'up' ? index - 1 : index + 1
        if (targetIndex < 0 || targetIndex >= newSections.length) return
        ;[newSections[index], newSections[targetIndex]] = [newSections[targetIndex], newSections[index]]
        const reordered = newSections.map((s, i) => ({ _id: s._id, order: i }))
        setSections(newSections.map((s, i) => ({ ...s, order: i })))
        try {
            await HitApi({ unitIds: unitId, sections: reordered }, reorderWebsiteSections)
        } catch { toast.error('Reorder failed'); fetchSections() }
    }

    const handleDeleteSection = async () => {
        if (!deleteTarget) return
        setIsDeleting(true)
        try {
            const res = await HitApi({ _id: deleteTarget._id }, deleteWebsiteSection)
            if (res?.success) {
                toast.success('Section deleted')
                setSections(prev => prev.filter(s => s._id !== deleteTarget._id))
                setDeleteTarget(null)
            } else toast.error('Failed to delete')
        } catch { toast.error('Failed to delete section') }
        setIsDeleting(false)
    }

    const handleEditSection = (section) => {
        setEditingSection(section)
        const content = section.content || DEFAULT_CONTENT[section.type] || {}
        setEditContent(content)
        if (section.type === 'cta') {
            dispatch(setApiJson({ ...ApiReducer?.apiJson, ctaBgImage: content.backgroundImage || '' }))
        }
    }

    const handleSaveSection = async () => {
        if (!editingSection) return
        setIsSavingSection(true)
        const content = editingSection.type === 'cta'
            ? { ...editContent, backgroundImage: ApiReducer?.apiJson?.ctaBgImage ?? editContent.backgroundImage ?? '' }
            : editContent
        try {
            const res = await HitApi({ _id: editingSection._id, content }, updateWebsiteSection)
            if (res?.success) {
                toast.success('Section updated!')
                setSections(prev => prev.map(s => s._id === editingSection._id ? { ...s, content } : s))
                setEditingSection(null)
            } else toast.error(res?.message || 'Failed to update section')
        } catch { toast.error('Failed to update section') }
        setIsSavingSection(false)
    }

    const handleAddSection = async () => {
        if (!unitId) return
        setIsAddingSection(true)
        try {
            const res = await HitApi({ unitIds: unitId, type: newSectionType, order: sections.length }, addWebsiteSection)
            if (res?.success) {
                toast.success('Section added!')
                setShowAddSection(false)
                fetchSections()
            } else toast.error(res?.message || 'Failed to add section')
        } catch { toast.error('Failed to add section') }
        setIsAddingSection(false)
    }

    const handleSeedDefaults = async () => {
        if (!window.confirm('Create all 6 default sections? Existing sections will be left unchanged.')) return
        try {
            const res = await HitApi({ unitIds: unitId }, seedDefaultWebsiteSections)
            if (res?.success) { toast.success('Default sections created!'); fetchSections() }
            else toast.error(res?.message || 'Failed to seed sections')
        } catch { toast.error('Failed to seed sections') }
    }

    // ── Edit Content Helpers ──────────────────────────────────────────────────

    const updateEditContent = (key, value) => setEditContent(prev => ({ ...prev, [key]: value }))

    const updateArrayItem = (arrayKey, index, field, value) => setEditContent(prev => {
        const arr = [...(prev[arrayKey] || [])]
        arr[index] = { ...arr[index], [field]: value }
        return { ...prev, [arrayKey]: arr }
    })

    const addArrayItem = (arrayKey, defaultItem) =>
        setEditContent(prev => ({ ...prev, [arrayKey]: [...(prev[arrayKey] || []), defaultItem] }))

    const removeArrayItem = (arrayKey, index) => setEditContent(prev => {
        const arr = [...(prev[arrayKey] || [])]
        arr.splice(index, 1)
        return { ...prev, [arrayKey]: arr }
    })

    // ─────────────────────────────────────────────────────────────────────────

    return (
        <div className="min-h-screen bg-gray-50">

            {/* ── Header ── */}
            <div className="bg-white border-b px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Globe className="w-6 h-6 text-gray-600" />
                        <div>
                            <h1 className="text-xl font-semibold text-gray-900">Website Configuration</h1>
                            <p className="text-sm text-gray-500">Control landing page content, theme and sections</p>
                        </div>
                    </div>
                    <div className="flex bg-gray-100 rounded-lg p-1">
                        <button
                            onClick={() => setActiveTab('settings')}
                            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${activeTab === 'settings' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            <Palette className="w-4 h-4" /> Global Settings
                        </button>
                        <button
                            onClick={() => setActiveTab('sections')}
                            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${activeTab === 'sections' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            <Layers className="w-4 h-4" /> Sections Manager
                        </button>
                    </div>
                </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════════
                GLOBAL SETTINGS TAB
            ══════════════════════════════════════════════════════════════════ */}
            {activeTab === 'settings' && (
                configLoading ? (
                    <div className="flex items-center justify-center py-32">
                        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                    </div>
                ) : (
                    <div className="p-6 max-w-4xl space-y-6">

                        {/* Branding & Colors */}
                        <div className="bg-white rounded-xl border p-6">
                            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <Palette className="w-4 h-4" /> Branding &amp; Colors
                            </h3>
                            <div className="grid grid-cols-2 gap-4 mb-5">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Theme Color</label>
                                    <div className="flex gap-2 items-center">
                                        <input type="color" value={config?.themeColor || '#000000'}
                                            onChange={(e) => updateConfigField('themeColor', e.target.value)}
                                            className="w-11 h-11 rounded-lg border border-slate-300 cursor-pointer p-0.5 flex-shrink-0" />
                                        <AppInput placeholder="#000000"
                                            value={config?.themeColor || '#000000'}
                                            onChangeInput={(e) => updateConfigField('themeColor', e.target.value)} />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Accent Color</label>
                                    <div className="flex gap-2 items-center">
                                        <input type="color" value={config?.accentColor || '#00C896'}
                                            onChange={(e) => updateConfigField('accentColor', e.target.value)}
                                            className="w-11 h-11 rounded-lg border border-slate-300 cursor-pointer p-0.5 flex-shrink-0" />
                                        <AppInput placeholder="#00C896"
                                            value={config?.accentColor || '#00C896'}
                                            onChangeInput={(e) => updateConfigField('accentColor', e.target.value)} />
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <FileuploadComp title="Logo (Light BG)" name="logoUrl" allowed={['img']} />
                                <FileuploadComp title="Logo (Dark BG)" name="logoUrlDark" allowed={['img']} />
                                <FileuploadComp title="Favicon" name="faviconUrl" allowed={['img']} />
                            </div>
                        </div>

                        {/* SEO */}
                        <div className="bg-white rounded-xl border p-6">
                            <h3 className="font-semibold text-gray-900 mb-4">SEO</h3>
                            <AppInput title="Page Title" placeholder="Elevate Lifestyle | Premium Salon & Spa"
                                value={config?.seo?.title || ''}
                                onChangeInput={(e) => updateConfigField('seo.title', e.target.value)} />
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description</label>
                                <textarea value={config?.seo?.description || ''}
                                    onChange={(e) => updateConfigField('seo.description', e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-blue-500 hover:border-slate-400 mb-4"
                                    rows={2} placeholder="Premium salon and spa services at your fingertips." />
                            </div>
                            <AppInput title="Keywords (comma-separated)" placeholder="salon, spa, hair, beauty, luxury"
                                value={config?.seo?.keywords || ''}
                                onChangeInput={(e) => updateConfigField('seo.keywords', e.target.value)} />
                        </div>

                        {/* Header Nav Links */}
                        <div className="bg-white rounded-xl border p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                                    <Link className="w-4 h-4" /> Header Nav Links
                                </h3>
                                <AppButton buttontext="Add Link" icon={Plus} iconPosition="left" size="sm" onClick={addNavLink} />
                            </div>
                            <div className="space-y-2">
                                {(config?.header?.navLinks || []).map((link, i) => (
                                    <div key={i} className="flex gap-2 items-center">
                                        <div className="flex-1">
                                            <AppInput placeholder="Label (e.g. Services)"
                                                value={link.label || ''} onChangeInput={(e) => updateNavLink(i, 'label', e.target.value)} />
                                        </div>
                                        <div className="flex-1">
                                            <AppInput placeholder="URL (e.g. #services)"
                                                value={link.href || ''} onChangeInput={(e) => updateNavLink(i, 'href', e.target.value)} />
                                        </div>
                                        <button onClick={() => removeNavLink(i)} className="text-red-400 hover:text-red-600 mb-4 flex-shrink-0">
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                                {!(config?.header?.navLinks || []).length && (
                                    <p className="text-sm text-gray-400 text-center py-4 border border-dashed border-gray-200 rounded-lg">
                                        No nav links. Add one above.
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Footer Info */}
                        <div className="bg-white rounded-xl border p-6">
                            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <MapPin className="w-4 h-4" /> Footer Info
                            </h3>
                            <div className="grid grid-cols-2 gap-x-4">
                                <AppInput title="Address" placeholder="123 Main Street, City, State"
                                    value={config?.footer?.address || ''}
                                    onChangeInput={(e) => updateConfigField('footer.address', e.target.value)} />
                                <AppInput title="Phone" placeholder="+91 98765 43210"
                                    value={config?.footer?.phone || ''}
                                    onChangeInput={(e) => updateConfigField('footer.phone', e.target.value)} />
                                <AppInput title="Email" placeholder="hello@elevate.in"
                                    value={config?.footer?.email || ''}
                                    onChangeInput={(e) => updateConfigField('footer.email', e.target.value)} />
                                <AppInput title="Working Hours" placeholder="Mon–Sun: 10AM – 8PM"
                                    value={config?.footer?.hours || ''}
                                    onChangeInput={(e) => updateConfigField('footer.hours', e.target.value)} />
                                <div className="col-span-2">
                                    <AppInput title="Copyright Text" placeholder="© 2025 Elevate Lifestyle. All rights reserved."
                                        value={config?.footer?.copyrightText || ''}
                                        onChangeInput={(e) => updateConfigField('footer.copyrightText', e.target.value)} />
                                </div>
                            </div>
                        </div>

                        {/* Social Links */}
                        <div className="bg-white rounded-xl border p-6">
                            <h3 className="font-semibold text-gray-900 mb-4">Social Links</h3>
                            <div className="grid grid-cols-2 gap-x-4">
                                {[
                                    { key: 'instagram', label: 'Instagram' },
                                    { key: 'facebook', label: 'Facebook' },
                                    { key: 'twitter', label: 'Twitter / X' },
                                    { key: 'youtube', label: 'YouTube' },
                                ].map(({ key, label }) => (
                                    <AppInput key={key} title={label} placeholder={`https://${key}.com/yourhandle`}
                                        value={config?.footer?.socialLinks?.[key] || ''}
                                        onChangeInput={(e) => updateSocialLink(key, e.target.value)} />
                                ))}
                            </div>
                        </div>

                        {/* Maintenance Mode */}
                        <div className="bg-white rounded-xl border p-6">
                            <div className="flex items-center justify-between">
                                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                                    <AlertCircle className="w-4 h-4 text-red-500" /> Maintenance Mode
                                </h3>
                                <div
                                    onClick={() => updateConfigField('maintenanceMode.enabled', !config?.maintenanceMode?.enabled)}
                                    className={`relative inline-block w-11 h-6 rounded-full cursor-pointer transition-colors ${config?.maintenanceMode?.enabled ? 'bg-red-500' : 'bg-gray-200'}`}
                                >
                                    <span className={`absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transform transition-transform ${config?.maintenanceMode?.enabled ? 'translate-x-5' : 'translate-x-0'}`} />
                                </div>
                            </div>
                            {config?.maintenanceMode?.enabled && (
                                <div className="mt-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Message shown to visitors</label>
                                    <textarea value={config?.maintenanceMode?.message || ''}
                                        onChange={(e) => updateConfigField('maintenanceMode.message', e.target.value)}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-blue-500 hover:border-slate-400"
                                        rows={2} placeholder="We're currently down for maintenance. Please check back soon." />
                                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                                        <p className="text-xs text-red-700 font-medium">Website is in maintenance mode — visitors will see this message.</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Save Button */}
                        <AppButton
                            buttontext={isSaving ? 'Saving...' : 'Save Configuration'}
                            onClick={handleSaveConfig}
                            isLoading={isSaving}
                            icon={Save}
                            iconPosition="left"
                            className="w-full justify-center py-3"
                            color="primary"
                        />

                    </div>
                )
            )}

            {/* ══════════════════════════════════════════════════════════════════
                SECTIONS MANAGER TAB
            ══════════════════════════════════════════════════════════════════ */}
            {activeTab === 'sections' && (
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-2">
                            <Layers className="w-5 h-5 text-gray-500" />
                            <h2 className="text-lg font-semibold text-gray-800">Page Sections</h2>
                            <span className="text-sm text-gray-400">({sections.length})</span>
                        </div>
                        <div className="flex gap-2">
                            <AppButton buttontext="Seed Defaults" icon={RefreshCw} iconPosition="left"
                                variant="outline" onClick={handleSeedDefaults} />
                            <AppButton buttontext="Add Section" icon={Plus} iconPosition="left"
                                color="primary" onClick={() => setShowAddSection(true)} />
                        </div>
                    </div>

                    {sectionsLoading ? (
                        <div className="flex items-center justify-center py-20">
                            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                        </div>
                    ) : sections.length === 0 ? (
                        <div className="text-center py-20 bg-white rounded-xl border">
                            <Layers className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                            <p className="text-gray-500 font-medium">No sections yet</p>
                            <p className="text-sm text-gray-400 mt-1">Seed the defaults to get started instantly</p>
                            <div className="mt-4">
                                <AppButton buttontext="Seed Default Sections" icon={RefreshCw} iconPosition="left"
                                    color="primary" onClick={handleSeedDefaults} />
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {sections.map((section, index) => (
                                <div key={section._id}
                                    className={`bg-white rounded-xl border p-4 flex items-center gap-4 transition-opacity ${!section.isVisible ? 'opacity-60' : ''}`}>
                                    {/* Reorder */}
                                    <div className="flex flex-col gap-0.5">
                                        <button onClick={() => handleMoveSection(index, 'up')} disabled={index === 0}
                                            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                                            <ChevronUp className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => handleMoveSection(index, 'down')} disabled={index === sections.length - 1}
                                            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                                            <ChevronDown className="w-4 h-4" />
                                        </button>
                                    </div>
                                    {/* Position Badge */}
                                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center text-xs font-bold text-gray-500 flex-shrink-0">
                                        {index + 1}
                                    </div>
                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-gray-900 text-sm">{SECTION_LABELS[section.type] || section.type}</p>
                                        <p className="text-xs text-gray-400 mt-0.5 truncate">
                                            {section.isVisible ? 'Visible' : 'Hidden'}
                                            {section.content?.title ? ` · ${section.content.title}` : ''}
                                            {section.content?.headline ? ` · ${section.content.headline}` : ''}
                                        </p>
                                    </div>
                                    {/* Actions */}
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        <button onClick={() => handleToggleVisibility(section)} title={section.isVisible ? 'Hide section' : 'Show section'}
                                            className={`p-2 rounded-lg transition-colors ${section.isVisible ? 'bg-green-50 text-green-600 hover:bg-green-100' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}>
                                            {section.isVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                        </button>
                                        <button onClick={() => handleEditSection(section)} className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100">
                                            <Pencil className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => setDeleteTarget(section)} className="p-2 bg-red-50 text-red-400 rounded-lg hover:bg-red-100">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ══════════════════════════════════════════════════════════════════
                ADD SECTION MODAL
            ══════════════════════════════════════════════════════════════════ */}
            <CustomModal isOpen={showAddSection} onClose={() => setShowAddSection(false)} size="md">
                <div className="border-b px-6 py-4 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900">Add Section</h2>
                    <button onClick={() => setShowAddSection(false)}><X className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
                </div>
                <div className="p-6 space-y-5">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Section Type</label>
                        <select value={newSectionType} onChange={(e) => setNewSectionType(e.target.value)}
                            className="w-full h-11 px-3 border border-slate-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 hover:border-slate-400 bg-transparent">
                            {SECTION_TYPES.map(t => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="flex gap-3">
                        <AppButton buttontext={isAddingSection ? 'Adding...' : 'Add Section'} isLoading={isAddingSection}
                            onClick={handleAddSection} color="primary" className="flex-1 justify-center" />
                        <AppButton buttontext="Cancel" variant="outline" onClick={() => setShowAddSection(false)} />
                    </div>
                </div>
            </CustomModal>

            {/* ══════════════════════════════════════════════════════════════════
                EDIT SECTION MODAL
            ══════════════════════════════════════════════════════════════════ */}
            <CustomModal
                isOpen={!!editingSection}
                onClose={() => { setEditingSection(null); setEditorFullscreen(false); }}
                size="2xl"
                fullscreen={editorFullscreen}
            >
                <div className={`bg-white border-b px-6 py-4 flex items-center justify-between ${editorFullscreen ? '' : 'sticky top-0 rounded-t-lg'}`}>
                    <h2 className="text-lg font-semibold text-gray-900">
                        Edit: {SECTION_LABELS[editingSection?.type] || editingSection?.type}
                    </h2>
                    <button onClick={() => { setEditingSection(null); setEditorFullscreen(false); }}>
                        <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                    </button>
                </div>
                <div className={editorFullscreen ? 'flex flex-col flex-1 overflow-hidden' : 'p-6'}>
                    {editingSection && (
                        <SectionContentEditor
                            type={editingSection.type}
                            content={editContent}
                            onChange={updateEditContent}
                            onArrayChange={updateArrayItem}
                            onAddItem={addArrayItem}
                            onRemoveItem={removeArrayItem}
                            isFullscreen={editorFullscreen}
                            onToggleFullscreen={() => setEditorFullscreen(f => !f)}
                        />
                    )}
                    {!editorFullscreen && (
                        <div className="flex gap-3 mt-4 pt-4 border-t">
                            <AppButton buttontext={isSavingSection ? 'Saving...' : 'Save Changes'} isLoading={isSavingSection}
                                icon={Check} iconPosition="left" onClick={handleSaveSection} color="primary" className="flex-1 justify-center" />
                            <AppButton buttontext="Cancel" variant="outline" onClick={() => setEditingSection(null)} />
                        </div>
                    )}
                    {editorFullscreen && (
                        <div className="flex gap-3 px-4 py-3 border-t bg-white flex-shrink-0">
                            <AppButton buttontext={isSavingSection ? 'Saving...' : 'Save Changes'} isLoading={isSavingSection}
                                icon={Check} iconPosition="left" onClick={handleSaveSection} color="primary" className="flex-1 justify-center" />
                            <AppButton buttontext="Cancel" variant="outline" onClick={() => { setEditingSection(null); setEditorFullscreen(false); }} />
                        </div>
                    )}
                </div>
            </CustomModal>

            {/* ══════════════════════════════════════════════════════════════════
                DELETE CONFIRMATION MODAL
            ══════════════════════════════════════════════════════════════════ */}
            <DeleteConfirmationModal
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDeleteSection}
                itemName={SECTION_LABELS[deleteTarget?.type] || deleteTarget?.type}
                itemType="section"
                isLoading={isDeleting}
            />

        </div>
    )
}

export default WebsiteConfiguration
